// @flow
import React, { type Node, useCallback } from 'react';
import cn from 'classnames/bind';

import PlainIcon, { iconTypes, colorTypes } from 'app/common/ui-next/plain-icon';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import { DotsLoader } from 'app/common/components/Loader';
import PlainTooltip from 'app/common/ui-next/plain-tooltip';

import type { ComplexDish } from '../../dataTypes/order/ComplexDish.types';
import ComplexDishItem from './ComplexDishItem';
import Stikers from './Stikers';

import styles from './ComplexItem.module.scss';;


const cx = cn.bind(styles);

type Props = {
  id: string,
  title: string,
  className?: string,
  checked?: boolean, // addedToOrder
  disabled?: boolean, // notAvailable
  disabledReason?: string,
  dishes: Array<ComplexDish>,
  currency: string,
  cost: number | null,
  loading?: boolean,
  exchangeTarget?: boolean,
  onCheck?: (string) => void,
  onUncheck?: (string) => void,
  isFree?: boolean,
  isNew?: boolean,
}

const ComplexItem = ({
  id,
  title,
  className,
  checked,
  disabled,
  disabledReason,
  dishes,
  currency,
  cost,
  loading,
  exchangeTarget,
  onCheck,
  onUncheck,
  isFree,
  isNew,
}: Props): Node => {
  const rootClassName = cx(styles.root, {
    available: !disabled && !checked,
    disabled,
    checked,
  }, className);

  const handleChange = useCallback((e) => {
    if (disabled) return;
    const { checked: newState } = e.currentTarget;
    if (newState && onCheck) {
      onCheck(id);
      return;
    }
    if (!newState && onUncheck) {
      onUncheck(id);
    }
  }, [onCheck, onUncheck, id, disabled]);

  const hasStikers = isFree || isNew;

  return (
    <label className={rootClassName}>
      <input
        type="checkbox"
        name="orderItem"
        value="title"
        checked={checked}
        className="visuallyHidden"
        onChange={handleChange}
      />

      <div className={styles.sidebar}>
        {hasStikers
          && <Stikers
            showFree={isFree}
            showNew={isNew}
            disabled={disabled}
          />}
        <PlainIcon
          width="38px"
          height="auto"
          type={iconTypes.COMPLEX}
          colorType={disabled ? colorTypes.COLOR_GRAY : colorTypes.COLOR_PRIMARY}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.title}>
          {title}
        </div>

        <div>
          {dishes.map(({ index, portion, title: dishTitle }) => (
            <ComplexDishItem
              key={index}
              index={index}
              title={dishTitle}
              portion={portion}
              disabledTooltips={disabled}
            />
          ))}
        </div>

        <div className={cx(styles.cost, { disabled })}>
          {cost !== null && toFixedValueCurrency(cost, false, currency)}
        </div>
      </div>

      {exchangeTarget
        && <div className={cx(styles.exchangeTarget, { disabled })}>
          <PlainIcon
            type={iconTypes.EXCHANGE_CIRCLE}
            colorType={colorTypes.COLOR_WHITE}
          />
        </div>}

      {loading
        && <DotsLoader className={styles.dotsLoader} />}

      {disabled
        && disabledReason
        && <PlainTooltip text={disabledReason}>
          <div className={styles.tooltip} />
        </PlainTooltip>}
    </label>
  );
};

export default ComplexItem;
